"use client";

import React from "react";
import { useInactivityTimeout } from "@/hooks/useInactivityTimeout";
import InactivityWarningDialog from "./InactivityWarningDialog";
import { useAuth } from "@/context/auth-context";
import { usePathname } from "next/navigation";
import {
    hasRecoverableAuthHints,
    isProtectedAppRoute,
    readAuthHintSnapshot,
} from "./authRecovery";

interface InactivityProviderProps {
    children: React.ReactNode;
}

export const InactivityProvider: React.FC<InactivityProviderProps> = ({
    children,
}) => {
    const { isAuthenticated } = useAuth();
    const pathname = usePathname() ?? "";
    const authHintSnapshot = readAuthHintSnapshot(
        typeof window === "undefined" ? null : window.localStorage,
        false
    );
    const shouldTreatProtectedSessionAsActive =
        isProtectedAppRoute(pathname) &&
        (isAuthenticated || hasRecoverableAuthHints(authHintSnapshot));

    const {
        isWarningShown,
        timeRemaining,
        isTimedOut,
        extendSession,
        forceLogout,
        config,
    } = useInactivityTimeout({
        enableWarning: true,
        enableInactivityDetection: true,
        sessionActive: shouldTreatProtectedSessionAsActive,
    });

    const showWarning =
        shouldTreatProtectedSessionAsActive && isWarningShown && !isTimedOut;

    return (
        <>
            {children}
            <InactivityWarningDialog
                show={showWarning}
                timeRemaining={timeRemaining}
                onExtendSession={extendSession}
                onLogoutNow={forceLogout}
                warningDuration={config.warningDuration}
            />
        </>
    );
};

export default InactivityProvider;
